import {
  Button,
  ButtonTargetKind,
  ButtonSize,
  ButtonRole,
} from "@components/Button";
import { ErrorMessage } from "@components/ErrorMessage";
import { Icon, IconSize, IconDisplay } from "@components/Icon";
import { Loading } from "@components/LoadingIndicator";
import { Modal } from "@components/Modal";
import { UserList } from "@components/UserListCard";
import styled from "@emotion/styled";
import React, { useContext, useState, useCallback } from "react";

import { UserResponse } from "@every.org/common/src/codecs/entities";
import { unfollowRouteSpec } from "@every.org/common/src/routes/follow";

import { useLoggedInUserOrUndefined } from "src/context/AuthContext/hooks";
import { UsersContext } from "src/context/UsersContext";
import {
  fetchFollowsForUser,
  FetchFollowsResult,
} from "src/context/UsersContext/actions";
import { getUser } from "src/context/UsersContext/selectors";
import { useAsyncEffect } from "src/hooks/useAsyncEffect";
import { colorCssVars } from "src/theme/color";
import { spacing } from "src/theme/spacing";
import { queryApi } from "src/utility/apiClient";
import { logger } from "src/utility/logger";

const LightSubText = styled.h4`
  color: var(${colorCssVars.text.secondary});
`;

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 400px;
`;

const ModalHeader = styled.h2`
  margin-bottom: ${spacing.oldM};
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

interface RemoveFollowersModalContentProps {
  user: UserResponse;
  onUnfollowed: (user: UserResponse) => void;
  onCancel: () => void;
}
const RemoveFollowersModalContent: React.FCC<
  RemoveFollowersModalContentProps
> = ({ user, onUnfollowed, onCancel }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>();
  const removeFollower = async () => {
    try {
      setIsSubmitting(true);
      await queryApi(unfollowRouteSpec, {
        body: { fromId: user.id },
        routeTokens: {},
        queryParams: {},
      });
      setIsSubmitting(false);
      onUnfollowed(user);
    } catch (e) {
      if (e instanceof Error) {
        setError(e.message);
      }
      setIsSubmitting(false);
    }
  };

  return (
    <ModalContainer>
      <ModalHeader>Remove Follower?</ModalHeader>
      <p>
        {`We won't tell ${
          user.firstName ? user.firstName : "them"
        } that they were removed from your followers.`}
      </p>
      <ErrorMessage text={error} />
      <ButtonContainer>
        <Button
          data-tname="removeFollowerCancel"
          role={ButtonRole.TEXT_ONLY}
          size={ButtonSize.SMALL}
          onClick={{
            kind: ButtonTargetKind.FUNCTION,
            action: () => {
              onCancel();
            },
          }}
        >
          Cancel
        </Button>
        <Button
          data-tname="removeFollowerConfirm"
          role={ButtonRole.SECONDARY}
          size={ButtonSize.SMALL}
          disabled={isSubmitting}
          onClick={{
            kind: ButtonTargetKind.FUNCTION,
            action: removeFollower,
          }}
        >
          Confirm
        </Button>
      </ButtonContainer>
    </ModalContainer>
  );
};

function renderUnfollowButton(
  user: UserResponse,
  onUserClicked: (user: UserResponse) => void
) {
  return (
    <Button
      data-tname={"removeFollower"}
      role={ButtonRole.UNSTYLED}
      onClick={{
        kind: ButtonTargetKind.FUNCTION,
        action: () => {
          onUserClicked(user);
        },
      }}
    >
      <Icon
        iconImport={() => import("@components/Icon/icons/XIcon")}
        size={IconSize.MEDIUM}
        display={IconDisplay.ACCENT}
      />
    </Button>
  );
}

interface FollowersListProps {
  userId: UserResponse["id"];
  onClick?: () => void;
  followerCount: number;
}
export const FollowersList: React.FCC<FollowersListProps> = ({
  userId,
  onClick,
  followerCount,
}) => {
  const usersState = useContext(UsersContext);
  const loggedInUser = useLoggedInUserOrUndefined();
  const isOwnFollowersList = loggedInUser?.id === userId;
  const [followerIds, setFollowerIds] = useState<UserResponse["id"][]>();
  const [selectedUser, setSelectedUser] = useState<UserResponse>();

  const fetch = useCallback(() => {
    return fetchFollowsForUser(userId);
  }, [userId]);

  const handleResponse = useCallback((result: FetchFollowsResult) => {
    setFollowerIds(result.followersIds);
  }, []);

  const handleError = useCallback((e: Error) => {
    logger.error({ message: "An error occurred fetching followers", error: e });
  }, []);

  useAsyncEffect({
    asyncOperation: fetch,
    handleResponse,
    handleError,
  });

  if (followerIds === undefined) {
    return <Loading />;
  }
  if (followerIds.length <= 0) {
    return (
      <LightSubText>
        {isOwnFollowersList
          ? "You have no followers."
          : followerCount
          ? "No public followers."
          : "No followers."}
      </LightSubText>
    );
  }

  const onUnfollowed = (user: UserResponse) => {
    setSelectedUser(undefined);
    setFollowerIds(followerIds.filter((id) => id !== user.id));
  };

  const onUserClicked = (user: UserResponse) => {
    setSelectedUser(user);
  };

  const followerUsers = followerIds
    .map((followerId) => getUser(usersState, { id: followerId }))
    .filter((user): user is UserResponse => !!user);

  return (
    <React.Fragment>
      <UserList
        usersInfo={followerUsers.map((user) => ({ user }))}
        showLocation
        onClick={onClick}
        renderChild={
          (isOwnFollowersList &&
            ((user) => renderUnfollowButton(user, onUserClicked))) ||
          undefined
        }
      />
      <Modal
        isOpen={!!selectedUser}
        contentLabel="Edit Privacy Modal"
        onRequestClose={() => {
          setSelectedUser(undefined);
        }}
      >
        {selectedUser && (
          <RemoveFollowersModalContent
            user={selectedUser}
            onUnfollowed={onUnfollowed}
            onCancel={() => {
              setSelectedUser(undefined);
            }}
          />
        )}
      </Modal>
    </React.Fragment>
  );
};
